import { Options, SeriesClickCallbackFunction, SeriesClickEventObject } from 'highcharts';

import { LayoutBounds } from '@utils/widgets/layout';
import { ChartOptionsBuilder } from '../../../../utils/charts/chart-options-builder';
import { ChartOptionFactory } from '../../../../utils/charts/chart-options-factory';
import { TreemapChartData } from './treemap-chart-data';
import { TreemapChartSettings } from './treemap-chart-settings';

// TODO get from settings or data
const currency = 'EUR';
const locale = new Intl.Locale('de-DE');

const formatterFull: Highcharts.DataLabelsFormatterCallbackFunction & Highcharts.TooltipFormatterCallbackFunction = function() {
  return `<span class="sd-meta sd-meta--size-sm">${
    this.point.name
  }</span><br><span class="sd-meta">${this.point.value.toLocaleString(locale)} ${currency}</span>`;
}

export class TreemapChartOptionFactory extends ChartOptionFactory<TreemapChartData, TreemapChartSettings> {
  constructor(private readonly clickCallback?: (key: string) => void, private readonly labelDisplayThresholdPercentual?: number, private readonly labelShortThresholdPercentual?: number) {
    super();
  }

  public override getOptions(
    data: TreemapChartData,
    settings?: TreemapChartSettings,
    layout?: LayoutBounds | undefined
  ): Options {

    let filter = undefined;
    let formatter: Highcharts.DataLabelsFormatterCallbackFunction = formatterFull;

    if (this.labelDisplayThresholdPercentual !== undefined) {
      const total: number = data.series.reduce((accumulator, series) => accumulator + series.value, 0);
      const labelThreshold: number = total * this.labelDisplayThresholdPercentual / 100;

      filter = {
        property: 'value',
        operator: '>',
        value: labelThreshold,
      }
    }

    if (this.labelShortThresholdPercentual !== undefined) {
      const total: number = data.series.reduce((accumulator, series) => accumulator + series.value, 0);
      const shortThreshold: number = total * this.labelShortThresholdPercentual / 100;

      formatter = function() {
        return this.point.value < shortThreshold ? '...' : formatterFull.apply(this);
      };
    }

    const options = new ChartOptionsBuilder(this.getDefaultOptions())
      .setType('treemap')
      .setSeries([
        {
          type: 'treemap',
          colorIndex: -1,
          data: data.series,
          borderRadius: 4,
          layoutAlgorithm: 'squarified',
          alternateStartingDirection: true,
          dataLabels: {
            style: {
              textOutline: 'none',
            },
            filter: filter,
            overflow: 'allow',
            crop: false,
          },
          levels: [
            {
              level: 1,
              dataLabels: {
                enabled: true,
                align: 'left',
                verticalAlign: 'top',
                formatter: formatter,
              },
            },
          ],
        },
      ])
      // Highcharts 11.x Treemap automaticaly sets a wrong font if no explicit font is set
      .setFontFamily('')
      .setLegendEnabled(false)
      .removeBorderSpacingMargin()
      .setTooltip({
        formatter: formatterFull,
      })
      .get();

    options.colorAxis = {
      minColor: 'rgb(236, 240, 249)',
      maxColor: 'rgb(236, 240, 249)',
    };

    if (typeof this.clickCallback === 'function') {
      const clickCallback = this.clickCallback;
      const clickEventHandler: SeriesClickCallbackFunction = function (event: SeriesClickEventObject) {
        const index: number = event.point.index;
        const keys = data.keys as string[];

        if (!keys) {
          return;
        }

        const key: string = keys[index];

        clickCallback(key);
      };

      for (const series of options.series) {
        series.events = {
          click: clickEventHandler,
        };
      }
    }

    return options;
  }
}
